import { Link } from "react-router-dom"
import MarketingLayout from "../../components/marketing/MarketingLayout"
import MarketingSeo from "../../components/marketing/MarketingSeo"
import { measurementStudioSalesMailto } from "../../components/marketing/salesLinks"
import { Button } from "../../components/ui"

const capabilityAreas = [
  {
    number: "01",
    title: "Tagging health",
    description: "See whether the measurement layer is present, consistent, and connected across every site.",
    items: [
      "Analytics identifier coverage",
      "Google Tag Manager container visibility",
      "Cross-site and account-level tag consistency",
      "Website crawl and implementation diagnostics",
    ],
  },
  {
    number: "02",
    title: "Analytics data quality",
    description: "Inspect the signals that determine whether teams can trust the data reaching their reports.",
    items: [
      "Event and conversion quality checks",
      "Hostname and page-location diagnostics",
      "Self-referral and traffic-quality review",
      "Channel classification and attribution signals",
    ],
  },
  {
    number: "03",
    title: "GA4 governance",
    description: "Keep properties aligned with the settings, access, and platform connections your organization expects.",
    items: [
      "Property and data-stream configuration",
      "User and administrator access review",
      "Google Ads links and cost signals",
      "Shared settings standards across accounts",
    ],
  },
  {
    number: "04",
    title: "Agency operations",
    description: "Turn account-by-account checks into a repeatable program your whole team can operate.",
    items: [
      "Client and account organization",
      "Shared QA checks across client accounts",
      "Scheduled checks and audit history",
      "Custom audit standards planned for Studio Scale",
    ],
  },
]

const operatingSteps = [
  ["1", "Set the checklist", "Choose the tagging, data-quality, and configuration checks every client should pass."],
  ["2", "Audit", "Run the same QA process across connected websites, properties, containers, and platforms."],
  ["3", "Compare", "See passing accounts, exceptions, and recurring problems in one shared view."],
  ["4", "Prioritize", "Turn every exception into a clear account-level action list for the team."],
]

export default function MarketingProduct() {
  return (
    <MarketingLayout>
      <MarketingSeo
        title="Analytics QA Platform for Agencies | Measurement Studio"
        description="Check tagging health, analytics data quality, GA4 configuration, and account consistency across every agency client."
        path="/product"
      />
      <section className="marketing-page-hero">
        <div className="marketing-container marketing-page-hero__inner">
          <p className="marketing-eyebrow">Measurement Studio platform</p>
          <h1>Everything your agency needs to catch measurement drift early.</h1>
          <p>
            Run consistent QA across tagging, analytics data, GA4 settings, and
            paid-media connections without rebuilding the audit for every client.
          </p>
          <div className="marketing-actions">
            <Button as="a" href={measurementStudioSalesMailto} size="lg">Email sales</Button>
            <Button as={Link} to="/pricing" variant="outline" size="lg">
              View program options
            </Button>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--first">
        <div className="marketing-container marketing-product-list">
          {capabilityAreas.map((area) => (
            <article className="marketing-product-card" key={area.number}>
              <div className="marketing-product-card__intro">
                <span>{area.number}</span>
                <div>
                  <h2>{area.title}</h2>
                  <p>{area.description}</p>
                </div>
              </div>
              <ul>{area.items.map((item) => <li key={item}>{item}</li>)}</ul>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section marketing-section--surface">
        <div className="marketing-container marketing-workflow-band">
          <div>
            <p className="marketing-eyebrow">A repeatable agency workflow</p>
            <h2>Set the standard once. Check every account against it.</h2>
            <p className="marketing-copy">
              Measurement Studio turns one-off client audits into a consistent
              QA process your whole team can follow.
            </p>
          </div>
          <ol>
            {operatingSteps.map(([number, title, description]) => (
              <li key={number}>
                <span>{number}</span>
                <div><strong>{title}</strong><p>{description}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="marketing-section marketing-section--cta">
        <div className="marketing-container marketing-cta-panel">
          <div>
            <p className="marketing-eyebrow">Design your agency QA program</p>
            <h2>Tell us what every client account should get right.</h2>
          </div>
          <Button as="a" href={measurementStudioSalesMailto} size="lg">Email sales</Button>
        </div>
      </section>
    </MarketingLayout>
  )
}

